// The web platform's TokenStore — the localStorage-backed implementation of
// the shared session seam.
//
// This is the concrete half of the injection the shared contract layer is
// built around: the shared module never names `localStorage`; the web app
// supplies it here, and a future React-Native app supplies a
// Keychain/Keystore-backed store of the same shape. The storage layout is the
// historical one (`wt_token` / `wt_identity` / `wt_user`, the user as JSON),
// so an already-signed-in browser keeps its session across this refactor.

import type { AuthResponse, SessionUser, TokenStore } from "@waytide/core";

const TOKEN_KEY = "wt_token";
const IDENTITY_KEY = "wt_identity";
const USER_KEY = "wt_user";

// SSR-safe access. The public landing is server-rendered (in tests) and now
// reads the session to switch its CTA (sign-in ⇄ dashboard), so a read must
// degrade to "signed out" rather than throw when there is no `localStorage`
// (no DOM); writes are user actions, so they simply no-op under SSR.
const ls = (): Storage | null =>
  typeof localStorage === "undefined" ? null : localStorage;

/** A `localStorage`-backed `TokenStore`. Reads/writes the same three keys the
 * web app has always used, so existing sessions survive the move to the
 * injected seam. */
export const webTokenStore: TokenStore = {
  getToken() {
    return ls()?.getItem(TOKEN_KEY) ?? null;
  },
  getIdentity() {
    return ls()?.getItem(IDENTITY_KEY) ?? null;
  },
  getUser(): SessionUser | null {
    const raw = ls()?.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as SessionUser;
    } catch {
      return null;
    }
  },
  setSession(resp: AuthResponse) {
    const s = ls();
    if (!s) return;
    s.setItem(TOKEN_KEY, resp.token);
    s.setItem(IDENTITY_KEY, resp.identity);
    s.setItem(USER_KEY, JSON.stringify(resp.user));
  },
  clear() {
    const s = ls();
    if (!s) return;
    s.removeItem(TOKEN_KEY);
    s.removeItem(IDENTITY_KEY);
    s.removeItem(USER_KEY);
  },
};
